import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Empty, Input, Select, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Phone, RotateCcw, Search, UserCheck } from 'lucide-react';
import Layout from '../../Layout';
import { MANAGER, agentsOf, assigneeOf, isDoNotCall, taskText, useCallCenter, type Callback } from '../CustomerDemo/callCenterStore';
import { meName, softphone } from '../CustomerDemo/softphone';
import Softphone from '../CustomerDemo/components/Softphone';
import { AssigneeSelect, RescheduleTaskButton, TaskBadges, TaskDoneButton } from '../CustomerDemo/components/TaskWidgets';
import { TASK_TAB, TASK_TYPE, filterTasks, sortTasks, taskCounts, taskTabOf, taskTypeOf, type TaskTab, type TaskType } from './cskhStats';
import { CustomerByPhone, PageHeader, dueText, fmtTime } from './shared';
import { useCskhDemoAutoload } from './demoData';

const TAB_STYLE: Record<TaskTab, { cls: string; active: string }> = {
  overdue: { cls: 'bg-rose-50 text-rose-700 border-rose-200', active: 'bg-rose-600 text-white border-rose-600' },
  today: { cls: 'bg-amber-50 text-amber-700 border-amber-200', active: 'bg-amber-500 text-white border-amber-500' },
  upcoming: { cls: 'bg-blue-50 text-blue-700 border-blue-200', active: 'bg-blue-600 text-white border-blue-600' },
  done: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', active: 'bg-emerald-600 text-white border-emerald-600' },
};

/** Danh sách việc CSKH: gọi lại, khiếu nại, bất thường, khảo sát, hỏi thăm sau dịch vụ */
export default function CskhTasksPage() {
  useCskhDemoAutoload();
  const s = useCallCenter();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as TaskTab) || 'overdue';
  const type = (params.get('type') as TaskType) || null;
  const assignee = params.get('assignee');
  const q = params.get('q') || '';
  const go = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };

  const now = Date.now();
  const me = meName();
  const base = filterTasks(s, { type, assignee, q });
  const counts = taskCounts(base, now);
  const list = sortTasks(base.filter(cb => taskTabOf(cb, now) === tab), tab);
  const assigneeOptions = useMemo(
    () => Array.from(new Set([me, ...agentsOf(s), MANAGER, ...s.callbacks.map(cb => assigneeOf(s, cb))])).map(v => ({ value: v, label: v === me ? `${v} (tôi)` : v })),
    [s, me],
  );
  const hasFilter = !!(type || assignee || q);

  const columns: ColumnsType<Callback> = [
    { title: 'KHÁCH HÀNG', key: 'c', width: 220, render: (_, cb) => <CustomerByPhone phone={cb.phone} /> },
    {
      title: 'CÔNG VIỆC', key: 'task',
      render: (_, cb) => (
        <div className="min-w-[220px] max-w-[420px]">
          <div className="flex flex-wrap gap-1 mb-1"><TaskBadges cb={cb} />{taskTypeOf(cb) === 'callback' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">GỌI LẠI</span>}</div>
          <div className="text-[13px] text-gray-700 dark:text-gray-300">{taskText(cb) || <span className="text-gray-400">Không có ghi chú</span>}</div>
          {cb.doneAt && (
            <div className="text-xs text-emerald-700 mt-1">✓ {cb.doneBy} · {fmtTime(cb.doneAt, now)} — {cb.result}{typeof cb.satisfied === 'boolean' && (cb.satisfied ? ' · Khách hài lòng' : ' · Khách chưa hài lòng')}</div>
          )}
        </div>
      ),
    },
    {
      title: 'HẠN', key: 'due', width: 150,
      render: (_, cb) => (
        <div>
          <div className="text-[13px] font-medium text-gray-800 dark:text-gray-200">{dayjs(cb.dueAt).format('HH:mm DD/MM')}</div>
          {!cb.doneAt && <div className={`text-[11px] ${cb.dueAt < now ? 'text-rose-600 font-semibold' : 'text-gray-400'}`}>{dueText(cb.dueAt, now)}</div>}
          {cb.doneAt && <div className={`text-[11px] ${cb.doneAt > cb.dueAt ? 'text-amber-600' : 'text-emerald-600'}`}>{cb.doneAt > cb.dueAt ? 'Xong trễ hạn' : 'Xong đúng hạn'}</div>}
        </div>
      ),
    },
    { title: 'NGƯỜI THỰC HIỆN', key: 'who', width: 170, render: (_, cb) => (cb.doneAt ? <span className="text-xs text-gray-500">{assigneeOf(s, cb)}</span> : <AssigneeSelect cb={cb} />) },
    {
      title: '', key: 'act', width: 230, fixed: 'right',
      render: (_, cb) => cb.doneAt ? null : (
        <div className="flex flex-wrap items-center gap-1.5 justify-end">
          <Tooltip title={isDoNotCall(s, cb.phone) ? 'Khách đang chặn gọi' : undefined}>
            <Button size="small" type="primary" icon={<Phone size={12} />} disabled={isDoNotCall(s, cb.phone)} onClick={() => softphone.dial(cb.phone, { callbackId: cb.id })}>Gọi</Button>
          </Tooltip>
          <RescheduleTaskButton cb={cb} />
          <TaskDoneButton cb={cb} />
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Công việc CSKH" desc="Việc gọi lại, khiếu nại, báo bất thường, khảo sát và hỏi thăm sau dịch vụ — xếp việc gấp lên đầu"
          extra={<Button icon={<UserCheck size={14} />} type={assignee === me ? 'primary' : 'default'} onClick={() => go({ assignee: assignee === me ? null : me })}>Việc của tôi</Button>} />

        <div className="flex flex-wrap items-center gap-3">
          <Input allowClear className="!w-full sm:!w-64" prefix={<Search size={14} className="text-gray-400" />} placeholder="Tìm tên, số điện thoại, nội dung"
            value={q} onChange={e => go({ q: e.target.value || null })} />
          <Select allowClear className="w-full sm:w-[190px]" placeholder="Loại việc" value={type || undefined} onChange={v => go({ type: v || null })}
            options={(Object.keys(TASK_TYPE) as TaskType[]).map(k => ({ value: k, label: TASK_TYPE[k] }))} />
          <Select allowClear showSearch optionFilterProp="label" className="w-full sm:w-[220px]" placeholder="Người thực hiện" value={assignee || undefined}
            onChange={v => go({ assignee: v || null })} options={assigneeOptions} />
          {hasFilter && (
            <button type="button" onClick={() => go({ type: null, assignee: null, q: null })} className="text-xs text-gray-500 hover:text-[#3e91ab] flex items-center gap-1 cursor-pointer">
              <RotateCcw size={12} /> Xoá bộ lọc
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {(Object.keys(TASK_TAB) as TaskTab[]).map(k => (
            <button key={k} type="button" onClick={() => go({ tab: k === 'overdue' ? null : k })}
              className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium border shrink-0 flex items-center gap-1.5 cursor-pointer transition ${tab === k ? TAB_STYLE[k].active : TAB_STYLE[k].cls}`}>
              {TASK_TAB[k]}
              <span className={`text-[11px] px-1.5 rounded-full ${tab === k ? 'bg-white/25' : 'bg-black/5'}`}>{counts[k]}</span>
            </button>
          ))}
        </div>

        <Table columns={columns} dataSource={list} rowKey="id" size="middle" scroll={{ x: 'max-content' }}
          rowClassName={cb => (!cb.doneAt && cb.dueAt < now ? 'bg-rose-50/40 dark:bg-rose-950/10' : '')}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={tab === 'overdue' ? 'Không có việc quá hạn 🎉' : 'Không có việc nào'} /> }}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, showTotal: (t, r) => `Hiển thị ${r[0]}–${r[1]} / ${t} việc` }} />
      </div>
      <Softphone />
    </Layout>
  );
}
